/* eslint-disable no-nested-ternary */
// Adapter layer: the backend returns machine-readable mapping keys and the
// StepReviewMapping component consumes a view model instead. Keep this mapping
// here so ImporterService stays a thin transport wrapper.

// The contact-method group lives in legacy contacts_migration and is not exposed
// by /api3/importer/jobs/<id>/mapping/, so the wizard validates it here.
// `other_phone` is intentionally excluded — the backend group does not include it.
export const CONTACT_METHOD_FIELDS = [
  'email',
  'phone',
  'cellphone',
  'work_phone',
  'work_email',
  'other_email',
];

export const getMappedTargetFields = (rows = []) =>
  new Set(
    rows
      .map((row) => row.target_field)
      .filter((field) => field && field !== 'ignore')
  );

const catalogHasContactMethodFields = (catalog = []) =>
  CONTACT_METHOD_FIELDS.some((key) =>
    catalog.some((field) => field.key === key)
  );

const ENTITIES_WITH_CONTACT_METHOD_RULE = ['contacts', 'owners'];

export const OWNERS_PROPERTY_REFERENCE_CODE_FIELD = 'property_reference_code';

export const getRequiredFieldsStatus = (
  rows = [],
  catalog = [],
  entity = null
) => {
  const mappedFields = getMappedTargetFields(rows);
  const requiresContactMethod =
    ENTITIES_WITH_CONTACT_METHOD_RULE.includes(entity) &&
    catalogHasContactMethodFields(catalog);
  const catalogHasPropertyReferenceCode = catalog.some(
    (field) => field.key === OWNERS_PROPERTY_REFERENCE_CODE_FIELD
  );
  const missingPropertyReferenceCode =
    entity === 'owners' &&
    catalogHasPropertyReferenceCode &&
    !mappedFields.has(OWNERS_PROPERTY_REFERENCE_CODE_FIELD);

  return {
    missingMandatory: catalog
      .filter((field) => field.mandatory && field.key !== 'ignore')
      .some((field) => !mappedFields.has(field.key)),
    missingContactMethod:
      requiresContactMethod &&
      !CONTACT_METHOD_FIELDS.some((key) => mappedFields.has(key)),
    missingPropertyReferenceCode,
  };
};

export const hasMissingRequiredFields = (
  rows = [],
  catalog = [],
  entity = null
) => {
  const status = getRequiredFieldsStatus(rows, catalog, entity);
  return (
    status.missingMandatory ||
    status.missingContactMethod ||
    status.missingPropertyReferenceCode
  );
};

export const VALIDATION_REASON_KEYS = {
  no_column_mapped: 'ImporterMappingValidationNoColumnMapped',
  missing_mandatory_field: 'ImporterMappingValidationMissingMandatoryField',
  duplicate_highlander_field:
    'ImporterMappingValidationDuplicateHighlanderField',
  unknown_target_field: 'ImporterMappingValidationUnknownTargetField',
  incomplete_value_mapping: 'ImporterMappingValidationIncompleteValueMapping',
};

export const resolveRowStatus = (row) => {
  if (row.target_field === 'ignore') {
    return 'ignored';
  }
  if (!row.target_field) {
    return 'review';
  }
  return row.status === 'review' ? 'review' : 'mapped';
};

export const isFieldLockedForRow = (field, row) =>
  Boolean(
    field.highlander &&
      field.used_by_column != null &&
      field.used_by_column !== row.source_column_index
  );

const normalizeRow = (row) => ({
  source_column_index: row.source_column_index,
  source_header: row.source_header,
  target_field: row.target_field ?? '',
  target_label: row.target_label ?? '',
  confidence: row.confidence ?? null,
  source: row.source ?? null,
  status: resolveRowStatus(row),
  needs_review: Boolean(row.needs_review),
  sample: row.sample ?? '',
});

const normalizeCatalog = (fields = []) =>
  fields.map((field) => ({
    key: field.key,
    label: field.label,
    type: field.type,
    mandatory: Boolean(field.mandatory),
    highlander: Boolean(field.highlander),
    used_by_column: field.used_by_column ?? null,
  }));

// GET wraps the entity block under `response[entity]`; PUT returns the same
// block flat at the top level with an `entity` field.
const extractMappingBlock = (response, entity) => {
  const nested = response?.[entity];
  if (nested?.mapping != null || nested?.fields != null) {
    return nested;
  }
  if (response?.entity === entity || response?.mapping != null) {
    return response;
  }
  return nested ?? {};
};

export const normalizeMapping = (response, entity) => {
  const block = extractMappingBlock(response, entity);
  return {
    recordCount: block.record_count != null ? block.record_count : undefined,
    counts: {
      all: block.counts?.all ?? 0,
      mapped: block.counts?.mapped ?? 0,
      review: block.counts?.review ?? 0,
      ignored: block.counts?.ignored ?? 0,
    },
    catalog: normalizeCatalog(block.fields),
    rows: (block.mapping ?? []).map(normalizeRow),
    validation: {
      valid: Boolean(block.validation?.valid),
      reasons: block.validation?.reasons ?? [],
    },
  };
};

export const buildMappingPayload = (rows = []) =>
  rows.map((row) => ({
    source_column_index: row.source_column_index,
    source_header: row.source_header,
    target_field: row.target_field ?? '',
    confidence: null,
  }));

export const applyTargetFieldChange = (
  rows,
  sourceColumnIndex,
  targetField,
  catalog = []
) => {
  const fieldMeta = catalog.find((field) => field.key === targetField);
  return rows.map((row) =>
    row.source_column_index === sourceColumnIndex
      ? {
          ...row,
          target_field: targetField,
          target_label: targetField === 'ignore' ? '' : fieldMeta?.label ?? '',
          status:
            targetField === 'ignore'
              ? 'ignored'
              : targetField
              ? 'mapped'
              : 'review',
          needs_review: false,
        }
      : row
  );
};

export const ignoreReviewRows = (rows = [], catalog = []) =>
  rows.reduce(
    (nextRows, row) =>
      row.status === 'review'
        ? applyTargetFieldChange(
            nextRows,
            row.source_column_index,
            'ignore',
            catalog
          )
        : nextRows,
    rows
  );
